import React, { useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { t } from "i18next";
import BookingGetters from "../../../store/onlineBooking/getters";
import BookingApi from "../../../API/Apis/BookingApi";

export interface StripeEmbeddedPaymentProps {
  /** PaymentIntent clientSecret from server — initialises Elements */
  clientSecret: string;
  reservationDbId: number;
  currentPriceToPay: number;
  totalAmount: number;
  onClose: () => void;
  publishableKey: string;
  onSuccess: (paymentIntentId: string) => void;
  isChargeOrDep: "C" | "D";
  priceId: string;
}

// ─── Inner form (must live inside <Elements>) ────────────────────────────────
interface InnerFormProps {
  reservationDbId: number;
  currentPriceToPay: number;
  totalAmount: number;
  onClose: () => void;
  onSuccess: (paymentIntentId: string) => void;
  isChargeOrDep: "C" | "D";
  priceId: string;
}

function StripePaymentForm({
  reservationDbId,
  currentPriceToPay,
  totalAmount,
  onClose,
  onSuccess,
  isChargeOrDep,
  priceId,
}: InnerFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const appSettings = BookingGetters.getAppSettings();

  const buttonBorderRadius = appSettings?.buttonBorderRadius || 0;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const returnUrl =
      window.location.origin +
      `/MovementRenewal/stripeSuccessUrl` +
      `?reservationId=${reservationDbId}` +
      `&amount=${currentPriceToPay}`;

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    const { error, paymentIntent } = result;

    if (error) {
      console.error(
        "[Stripe] ERROR type:",
        error.type,
        "code:",
        error.code,
        "msg:",
        error.message
      );
      setErrorMessage(error.message || t("Payment failed. Please try again."));
      setIsProcessing(false);
      return;
    }

    if (!paymentIntent) {
      console.warn(
        "[Stripe] paymentIntent is NULL — Stripe redirected to:",
        returnUrl
      );
      return;
    }

    if (
      paymentIntent.status === "succeeded" ||
      paymentIntent.status === "processing" ||
      paymentIntent.status === "requires_capture"
    ) {
      // Save payment details to MyRent (fire-and-forget — payment already succeeded)
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const yy = now.getFullYear();
      const mm = pad(now.getMonth() + 1);
      const dd = pad(now.getDate());
      const hh = pad(now.getHours());
      const min = pad(now.getMinutes());
      const ss = pad(now.getSeconds());
      const paymentDate = `${yy}-${mm}-${dd} ${hh}:${min}:${ss}`;
      const amountInEuros = ((paymentIntent.amount || 0) / 100).toFixed(2);

      BookingApi.saveStripeDirectPaymentDetails({
        payment_intent_id: paymentIntent.id,
        client_secret: paymentIntent.client_secret || "",
        total_amount: String(totalAmount),
        amount_received: amountInEuros,
        payment_method_type:
          (paymentIntent as any).payment_method_types?.[0] || "card",
        payment_date: paymentDate,
        rental_id: "",
        reservation_id: String(reservationDbId),
        payment_intent_status: paymentIntent.status,
        payment_status: paymentIntent.status,
        currency: paymentIntent.currency || "eur",
        isCharge: isChargeOrDep === "C" ? "true" : "false",
        isDeposit: isChargeOrDep === "D" ? "true" : "false",
        price_id: priceId || paymentIntent.id,
      }).catch((err) =>
        console.error("[Stripe] saveStripeDirectPaymentDetails failed:", err)
      );

      onSuccess(paymentIntent.id);
    } else {
      console.error(
        "[Stripe] UNHANDLED STATUS:",
        paymentIntent.status,
        "— onSuccess NOT called"
      );
      setErrorMessage(t("Payment failed. Please try again."));
      setIsProcessing(false);
    }
  };

  const currencySymbol =
    appSettings?.defaultCurrency === "USD"
      ? "$"
      : appSettings?.defaultCurrency === "GBP"
      ? "£"
      : "€";
  return (
    <div>
      {!isReady && (
        <div className="flex flex-col gap-3 py-4">
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
          <div className="h-10 bg-gray-100 rounded animate-pulse w-1/2" />
        </div>
      )}

      <PaymentElement
        options={{ layout: "tabs" }}
        onReady={() => setIsReady(true)}
      />

      {errorMessage && (
        <p className="text-red-500 text-sm mt-3">{errorMessage}</p>
      )}

      <div className="flex justify-between items-center mt-5 mb-4 px-1">
        <span className="text-sm text-gray-600">{t("Total")}</span>
        <span className="text-lg font-bold text-gray-900">
          {currencySymbol}
          {currentPriceToPay.toFixed(2)}
        </span>
      </div>

      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 h-12 rounded border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors"
          style={{
            borderRadius: `${buttonBorderRadius}px`,
          }}
        >
          {t("Back")}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isProcessing || !stripe}
          className="flex-1 h-12 rounded text-white text-sm font-semibold disabled:opacity-50 transition-colors"
          style={{
            borderRadius: `${buttonBorderRadius}px`,

            backgroundColor: "var(--primary-color, #1976d2)",
          }}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              {t("Processing...")}
            </span>
          ) : (
            t("Pay now")
          )}
        </button>
      </div>

      <p className="text-xs text-gray-400 text-center mt-4">
        {t("Your payment is secured by Stripe")} 🔒
      </p>
    </div>
  );
}

// ─── Stripe promise cache ─────────────────────────────────────────────────────
const stripePromiseCache: Record<string, ReturnType<typeof loadStripe>> = {};
function getStripePromise(pk: string) {
  if (!stripePromiseCache[pk]) stripePromiseCache[pk] = loadStripe(pk);
  return stripePromiseCache[pk];
}

// ─── Outer component ─────────────────────────────────────────────────────────
//   • clientSecret comes from server BEFORE this component renders
//   • Elements is initialised with clientSecret — no deferred intent complexity
//   • stripe.confirmPayment needs no clientSecret — Elements already has it
export function StripeEmbeddedPayment({
  clientSecret,
  reservationDbId,
  currentPriceToPay,
  totalAmount,
  onClose,
  onSuccess,
  publishableKey,
  isChargeOrDep,
  priceId,
}: StripeEmbeddedPaymentProps) {
  const stripePromise = publishableKey
    ? getStripePromise(publishableKey)
    : null;

  return (
    <div className="w-full mt-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">
          {t("Complete your payment")}
        </h3>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          🔒 {t("Secured by Stripe")}
        </span>
      </div>

      {!publishableKey && (
        <p className="text-red-500 text-sm py-4">
          Stripe publishable key is missing. Add{" "}
          <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to your .env and
          restart.
        </p>
      )}

      {publishableKey && clientSecret && (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: { theme: "stripe" },
          }}
        >
          <StripePaymentForm
            reservationDbId={reservationDbId}
            currentPriceToPay={currentPriceToPay}
            totalAmount={totalAmount}
            onClose={onClose}
            onSuccess={onSuccess}
            isChargeOrDep={isChargeOrDep}
            priceId={priceId}
          />
        </Elements>
      )}
    </div>
  );
}

export default StripeEmbeddedPayment;
